import {
    add,
    differenceInYears,
    format as DateFnsFormat,
    parse,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
export const DEFAULT_DATE_FORMAT_DASH = 'MM-dd-yyyy';
export const SHORT_MONTH_DATE_FORMAT_SLASH = 'MM/dd/yy';
export const ISO_DATE_ONLY_FORMAT = 'yyyy-MM-dd';
export const SHORT_READABLE_DATE_FORMAT = 'MMM dd, yyyy';
export const READABLE_MONTH_DAY_FORMAT = 'MMMM do';

export const TIME_FORMAT = 'h:mm aaa';
export const TIME_FORMAT_WITH_TIMEZONE = 'h:mma z';

export const AMBIGUOUS_DATE_FORMAT = 'MM/dd/yyyy';
export const DEFAULT_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/; // MM/DD/YYYY

export const DAY_OF_WEEK_FORMAT = 'EEEE'; // Monday, Tuesday, etc.

export const convertToDate = (date: Date | string) =>
    date instanceof Date
        ? date
        : DEFAULT_DATE_REGEX.exec(date)
          ? parse(date, AMBIGUOUS_DATE_FORMAT, new Date()) // Specifically parse potentially ambiguous date format
          : new Date(date);

export const format = (date: Date | string, dateFormat?: string): string => {
    date = convertToDate(date);
    return DateFnsFormat(date, dateFormat ?? DEFAULT_DATE_FORMAT);
};

export const formatWithoutTimezone = (
    date: Date | string,
    dateFormat?: string,
): string => {
    date = convertToDate(date);

    // strips 'Z' from date to remove timezone
    return DateFnsFormat(
        new Date(date.toISOString().slice(0, -1)),
        dateFormat ?? DEFAULT_DATE_FORMAT,
    );
};

export const formatInTimezone = (date: Date | string, dateFormat: string) => {
    date = convertToDate(date);

    return formatInTimeZone(
        date,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat,
    );
};

export const formatDateTime = (date: Date | string) => {
    date = convertToDate(date);
    return DateFnsFormat(date, 'MM/dd/yyyy hh:mmaaa');
};

export const formatReadableMonthDay = (date: Date | string) => {
    date = convertToDate(date);
    return DateFnsFormat(date, READABLE_MONTH_DAY_FORMAT);
};

export const formatReadableMonthDayInTimezone = (date: Date | string) => {
    return formatInTimezone(date, READABLE_MONTH_DAY_FORMAT);
};

export const diffInYearsFromNow = (date: Date | string): number => {
    date = convertToDate(date);
    return differenceInYears(new Date(), date);
};

export const tomorrow = (): Date => {
    return add(new Date(), { days: 1 });
};

export const isoDateToLocalTimezoneDate = (isoDate: string): Date => {
    return new Date(`${isoDate}T00:00:00`); // Z missing so date interpreted in browser timezone
};

export const isoDateToUTCTimezoneDate = (isoDate: string): Date => {
    return new Date(`${isoDate}T00:00:00Z`);
};

export const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th'; // Covers 11th, 12th, 13th
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

export const formatDateToOrdinal = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    return `${day}${suffix}`;
};

export const formatAsMonthDayYearUTC = (date: Date): string => {
    return formatInTimeZone(date, 'UTC', 'MMM d, yyyy');
};

export const getMidnightUTCDate = (date: Date): Date => {
    // Get the local timezone offset in minutes and convert it to milliseconds
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    // Subtract the offset from the UTC date to get the local date
    const dateMinusOffset = new Date(date.getTime() - timezoneOffset);
    const dateWithoutTimezone = formatWithoutTimezone(dateMinusOffset);
    return isoDateToUTCTimezoneDate(dateWithoutTimezone);
};

export const getCurrentMonthName = () =>
    new Date().toLocaleString('default', { month: 'long' });
